import { useCallback } from 'react';
import { notification } from 'antd';
import { useGetTemplateQuery } from '@/state/slices/reports';
import { useUpdateTemplateMutation } from '@/state/slices/reportTemplates';
import { useWorksheetEditorState } from '../hooks/useWorksheetEditorState';
import { EditorShell } from '../WorksheetEditor/EditorShell';
import { EditorKindProvider } from '../WorksheetEditor/editorOptions';
import { TemplatePreviewPanel } from './TemplatePreviewPanel';

interface TemplateEditorProps {
  uuid: string;
}

/**
 * Editor for the report template that drives the PDF. Same sidebar and canvas
 * as the worksheet editor, plus a live PDF preview of the unsaved edits.
 */
export function TemplateEditor({ uuid }: TemplateEditorProps) {
  const { data, isLoading, error } = useGetTemplateQuery(uuid);
  const [updateTemplate, { isLoading: isSaving }] = useUpdateTemplateMutation();

  const editor = useWorksheetEditorState(data);
  const { worksheet: template, getPayload, markSaved } = editor;

  const handleSave = useCallback(async () => {
    const payload = getPayload();
    if (!payload) return;
    try {
      markSaved(await updateTemplate(payload).unwrap(), payload);
      notification.success({ title: 'Report template saved' });
    } catch (err) {
      notification.error({
        title: 'Failed to save report template',
        description: (err as { data?: { name?: string[] } })?.data?.name?.[0],
      });
    }
  }, [getPayload, updateTemplate, markSaved]);

  return (
    <EditorKindProvider value="template">
      <EditorShell
        editor={editor}
        isLoading={isLoading}
        hasError={Boolean(error)}
        loadErrorTitle="Failed to load report template"
        isSaving={isSaving}
        onSave={handleSave}
        aside={template && <TemplatePreviewPanel template={template} />}
      />
    </EditorKindProvider>
  );
}
